import React, { useState } from 'react';

import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/pro-solid-svg-icons';

import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';
import { Tooltip } from '@buildoutinc/blueprint-react/ui/Tooltip';

import PartnerToggle from 'components/users/syndication/partner_toggle';

import type { PropertyTypeIcon, SyndicationPartner } from 'components/users/types';

const EMAIL_DISABLED_TOOLTIP =
  'In order to edit the Syndication email addresses for this channel, please use the toggle to disconnect the ' +
  'channel first.';

interface Props {
  partner: SyndicationPartner;
  propertyTypes: PropertyTypeIcon[];
}

export default function PartnerCard({ partner, propertyTypes }: Props) {
  const { partnerToggleProps, emailCredential } = partner;
  const isEmail = partner.mechanism === 'email';

  const [connected, setConnected] = useState(partnerToggleProps?.initiallyConnected ?? false);
  const [emailTo, setEmailTo] = useState(emailCredential?.to ?? '');
  const [emailCc, setEmailCc] = useState(emailCredential?.cc ?? '');

  function handleResynced(res: { to: string | null; cc: string | null }) {
    setEmailTo(res.to ?? '');
    setEmailCc(res.cc ?? '');
  }

  return (
    <div className="col-md-6">
      {partner.premium ? (
        <div className="bg-buildout-blue-800 rounded-top ps-3" style={{ width: '191px' }}>
          <p className="text-buildout-blue-50 ls-2 py-2 m-0">
            <FontAwesomeIcon className="me-3" icon={faStar} />
            <b>Premium Partner</b>
          </p>
        </div>
      ) : partner.unaffiliated && partner.unaffiliatedLabel ? (
        <div className="bg-storm-grey-200 rounded-top ps-3" style={{ width: '66%' }}>
          <p className="text-storm-grey-900 py-2 m-0">
            {partner.unaffiliatedLabel.bold ? (
              <b>{` ${partner.unaffiliatedLabel.text}`}</b>
            ) : (
              partner.unaffiliatedLabel.text
            )}
          </p>
        </div>
      ) : (
        <div className="ps-3 invisible">
          <div className="text-uppercase ls-1 py-2">Syndication Partner</div>
        </div>
      )}

      <div
        className={classNames('card mb-4', {
          'rounded-0 border-3 rounded-end rounded-bottom border-buildout-blue-800': partner.premium,
        })}
      >
        <div className="d-flex justify-content-between align-items-center mt-4 mx-4">
          <div className="d-flex flex-column">
            <div>
              {partner.isCrexi ? (
                <h2 className="partner-logo-text m-0">CREXI</h2>
              ) : (
                partner.logo && <img alt={partner.name} className="partner-logo d-block" src={partner.logo} />
              )}
            </div>
            {partner.signupUrl && partner.signupHost && (
              <small className="partner-url d-block">
                <a href={partner.signupUrl}>{partner.signupHost}</a>
              </small>
            )}
          </div>

          {partnerToggleProps && (
            <PartnerToggle
              {...partnerToggleProps}
              connected={connected}
              emailCc={emailCc}
              emailTo={emailTo}
              onConnectedChange={setConnected}
              onResynced={handleResynced}
            />
          )}
        </div>

        <hr className="text-muted" />

        <div className="syndication-card-content">
          {partner.valueAddText && (
            <div className="px-4">
              <div className="row" dangerouslySetInnerHTML={{ __html: partner.valueAddText }} />
            </div>
          )}

          <div className="px-4 pt-2">
            <div className="row">
              <div className="col-6">
                {partner.valueAddBullets && (
                  <div
                    className="partner-bullet-list bg-buildout-blue-100 rounded overflow-auto py-2 px-3"
                    dangerouslySetInnerHTML={{ __html: partner.valueAddBullets }}
                  />
                )}
              </div>
              <div className="col-6">
                <table className="table table-responsive">
                  <tbody>
                    <tr>
                      <td className="fw-bold">Countries</td>
                      <td>
                        {partner.countries.length === 0
                          ? 'Any'
                          : partner.countries.map(country => (
                            <React.Fragment key={country}>
                              {country}
                              <br />
                            </React.Fragment>
                          ))}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Listing Type</td>
                      <td>{partner.listingType}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Photos</td>
                      <td>
                        {partner.photoPreferenceLabel}
                        {isEmail && (
                          <p className="text-muted small mb-0">(Capped at 25MB per email)</p>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="m-4 p-4 bg-storm-grey-100 rounded">
          <strong className="mb-2">Property Types</strong>
          <div className="pt-2 d-flex justify-content-between">
            <Tooltip.Provider>
              {propertyTypes.map(propertyType => (
                <Tooltip key={propertyType.id}>
                  <Tooltip.Trigger
                    render={(
                      <div
                        className={classNames('property-type-icon', {
                          'not-accepted': !partner.acceptedPropertyTypeIds.includes(propertyType.id),
                        })}
                        dangerouslySetInnerHTML={{ __html: propertyType.svg }}
                      />
                    )}
                  />
                  <Tooltip.Content side="bottom">
                    {propertyType.buildoutName}
                  </Tooltip.Content>
                </Tooltip>
              ))}
            </Tooltip.Provider>
          </div>
        </div>

        {isEmail && (
          <div className="p-4">
            <strong className="d-block mb-2">Account Rep Email Addresses</strong>
            <Tooltip disabled={!connected}>
              <Tooltip.Trigger render={<div className="form" />}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <Label htmlFor={`email-to-${partner.id}`}>To:</Label>
                  <Input
                    disabled={connected}
                    id={`email-to-${partner.id}`}
                    placeholder="Enter account rep email address"
                    value={emailTo}
                    onChange={e => setEmailTo(e.target.value)}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Label htmlFor={`email-cc-${partner.id}`}>Cc:</Label>
                  <Input
                    disabled={connected}
                    id={`email-cc-${partner.id}`}
                    placeholder="Enter account rep email address"
                    value={emailCc}
                    onChange={e => setEmailCc(e.target.value)}
                  />
                </div>
              </Tooltip.Trigger>
              <Tooltip.Content side="bottom">{EMAIL_DISABLED_TOOLTIP}</Tooltip.Content>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}
